import _ from "lodash"
import orderBy from "lodash/orderBy"
import { useMemo, useCallback } from "react"

import {
  DropdownItemType,
  GroupBySelectorProps,
} from "@costory/types/endpoints/DropdownTypes"

import BaseDropdownSelector from "@costory/front/components/dropdown/BaseDropdownSelector"
import RightPanel from "@costory/front/components/dropdown/RightPanel"
import { useAxesQuery } from "@costory/front/queries/axes"
import { useSuggestedAxesDataQuery } from "@costory/front/queries/suggestedAxes"

const GroupBySelector = ({
  groupBy: selectedDimension,
  setGroupBy,
  groupByOptions,
  whereClause,
  clearable = false,
}: GroupBySelectorProps) => {
  const { data: axes = [] } = useAxesQuery()
  const suggestedAxesDataQuery = useSuggestedAxesDataQuery({
    whereClause: whereClause || { combinator: "and", rules: [] },
  })

  // Add suggested axes to the options
  const allGroupByOptions = useMemo(() => {
    const suggestedAxes = suggestedAxesDataQuery.data ?? []

    // SQL already provides the correct ordering, so we can use the data as-is
    const suggestedGroup = {
      group: "Suggested",
      items: Array.isArray(suggestedAxes)
        ? suggestedAxes.reduce((acc, axis) => {
            if (typeof axis.name === "string" && axis.name.trim() !== "") {
              const foundAxis = axes.find((a) => a.name === axis.name)
              acc.push({
                value: axis.name,
                label: foundAxis?.label || axis.name,
                isSuggested: true,
                isGrayedOut: axis.category === "grayed_out",
              })
            }
            return acc
          }, [] as DropdownItemType[])
        : [],
    }

    const groupByOptionsWithSuggestedKey = groupByOptions.map((group) => {
      return {
        ...group,
        items: group.items.map((item) => ({
          ...item,
          isSuggested: suggestedGroup.items.some(
            (suggestedItem) => suggestedItem.value === item.value,
          ),
        })),
      }
    })

    const sortedGroupByOptions = _.sortBy(
      groupByOptionsWithSuggestedKey,
      (item) => (item.group === "Datasources" ? 1 : 0),
    )

    return [...sortedGroupByOptions, suggestedGroup]
  }, [groupByOptions, suggestedAxesDataQuery.data, axes])

  // Right panel renderer for group by selector
  const renderRightPanel = (selectedItem?: DropdownItemType) => {
    if (!selectedItem) return null
    if (!!suggestedAxesDataQuery.data?.length && !selectedItem.isSuggested)
      return null
    const rightPanelValue = selectedItem.value
    const rightPanelField = axes.find((field) => field.name === rightPanelValue)
    const suggestedAxes = suggestedAxesDataQuery.data ?? []
    const rightPanelSuggested = suggestedAxes.find(
      (axis) => axis.name === rightPanelValue,
    )

    const sampleValues =
      rightPanelSuggested && (rightPanelSuggested.values ?? []).length > 0
        ? orderBy(rightPanelSuggested.values ?? [], ["name"], ["asc"])
            .slice(0, 5)
            .map((v) => v.name)
        : orderBy(
            (rightPanelField?.values ?? []).map((v) => ({
              label: v.label || v.name,
              proportion: v.proportionCostAllocated,
            })),
            ["proportion"],
            ["desc"],
          )
            .slice(0, 5)
            .map((v) => v.label)

    return (
      <RightPanel
        item={selectedItem}
        sampleValues={sampleValues}
        noDataMessage="No sample values available"
      />
    )
  }
  const getAllSampleValuesForItem = useCallback(
    (item: DropdownItemType): string[] => {
      const rightPanelValue = item.value
      const rightPanelField = axes.find(
        (field) => field.name === rightPanelValue,
      )
      const suggestedAxes = suggestedAxesDataQuery.data ?? []
      const rightPanelSuggested = suggestedAxes.find(
        (axis) => axis.name === rightPanelValue,
      )

      if (
        rightPanelSuggested &&
        (rightPanelSuggested.values ?? []).length > 0
      ) {
        return orderBy(rightPanelSuggested.values ?? [], ["name"], ["asc"])
          .map((v) => v.name)
          .filter((name) => name && name.trim() !== "") // Filter out empty/null values
      }

      return orderBy(
        (rightPanelField?.values ?? []).map((v) => ({
          label: v.label || v.name,
          proportion: v.proportionCostAllocated,
        })),
        ["proportion"],
        ["desc"],
      )
        .map((v) => v.label)
        .filter((label) => label && label.trim() !== "") // Filter out empty/null values
    },
    [axes, suggestedAxesDataQuery.data],
  )
  // Custom search function to include sample values in search
  const customSearchFunction = useCallback(
    (item: DropdownItemType, query: string): boolean => {
      // Early return for empty or invalid queries
      if (!query || query.trim() === "") {
        return false
      }

      const sampleValues = getAllSampleValuesForItem(item)

      // Early return if no sample values available
      if (sampleValues.length === 0) {
        return false
      }

      const normalizedQuery = query.toLowerCase().trim()

      return sampleValues.some((sampleValue) => {
        // Handle null/undefined values gracefully
        if (!sampleValue || typeof sampleValue !== "string") {
          return false
        }

        return sampleValue.toLowerCase().includes(normalizedQuery)
      })
    },
    [getAllSampleValuesForItem],
  )
  return (
    <BaseDropdownSelector
      value={selectedDimension}
      onChange={setGroupBy}
      options={allGroupByOptions}
      label="Group By"
      placeholder="Select group by"
      width={750}
      dropdownHeight={350}
      inputWidth={250}
      clearable={clearable}
      hasSuggestedItems={!!suggestedAxesDataQuery.data?.length}
      suggestedQuery={suggestedAxesDataQuery}
      renderRightPanel={renderRightPanel}
      customSearchFunction={customSearchFunction}
    />
  )
}

export default GroupBySelector
